import { driverApplicationMatchesSearch } from './admin-driver-search';
import { normalizeDriverApplication } from './driver-applications';

export const RATING_LABELS = {
  1: 'Terrible',
  2: 'Poor',
  3: 'Okay',
  4: 'Good',
  5: 'Excellent',
};

export function ratingLabel(stars) {
  const n = Math.round(Number(stars));
  return RATING_LABELS[n] || '';
}

export function formatRatingAverage(value) {
  if (value == null || !Number.isFinite(value)) return null;
  return Math.round(value * 100) / 100;
}

/** Auth profile ids used on ride_requests / ride_ratings.driver_id */
export function driverRatingIdentityKeys(driver) {
  const keys = new Set();
  if (driver?.id) keys.add(String(driver.id));
  if (driver?.app_profile_id) keys.add(String(driver.app_profile_id));
  return [...keys];
}

function normalizeReviewFromRatingRow(row) {
  return {
    rideId: String(row.ride_id || ''),
    driverId: String(row.driver_id || ''),
    riderId: String(row.rider_id || ''),
    stars: Number(row.stars),
    feedback: String(row.feedback || '').trim(),
    ratedAt: row.created_at || null,
    source: 'ride_ratings',
  };
}

function normalizeReviewFromRideRequest(row) {
  const stars = Number(row.rider_rating);
  if (!Number.isFinite(stars) || stars < 1 || stars > 5) return null;
  return {
    rideId: String(row.id || ''),
    driverId: String(row.driver_id || ''),
    riderId: String(row.rider_id || ''),
    stars,
    feedback: String(row.rider_feedback || '').trim(),
    ratedAt: row.rated_at || null,
    pickup: String(row.pickup_address || row.pickup || '').trim(),
    dropoff: String(row.dropoff_address || row.dropoff || '').trim(),
    source: 'ride_requests',
  };
}

function dedupeReviews(reviews) {
  const byRide = new Map();
  reviews.forEach((review) => {
    const key = review.rideId || `${review.ratedAt}-${review.stars}-${review.feedback}`;
    const existing = byRide.get(key);
    if (!existing || review.source === 'ride_ratings') {
      byRide.set(key, review);
    }
  });
  return [...byRide.values()].sort(
    (a, b) => Date.parse(b.ratedAt || 0) - Date.parse(a.ratedAt || 0)
  );
}

function summarizeReviews(reviews) {
  const list = reviews.filter((r) => r.stars >= 1 && r.stars <= 5);
  const reviewCount = list.length;
  if (!reviewCount) {
    return {
      reviewCount: 0,
      averageRating: null,
      lowRatingCount: 0,
      recentLowCount: 0,
      lastRatedAt: null,
    };
  }

  const sum = list.reduce((acc, r) => acc + r.stars, 0);
  const averageRating = formatRatingAverage(sum / reviewCount);
  const lowRatingCount = list.filter((r) => r.stars <= 2).length;

  const thirtyDaysAgo = Date.now() - 30 * 24 * 60 * 60 * 1000;
  const recentLowCount = list.filter(
    (r) => r.stars <= 2 && Date.parse(r.ratedAt || 0) >= thirtyDaysAgo
  ).length;

  const lastRatedAt = list.reduce((latest, r) => {
    const t = Date.parse(r.ratedAt || 0);
    if (!Number.isFinite(t)) return latest;
    if (!latest || t > Date.parse(latest)) return r.ratedAt;
    return latest;
  }, null);

  return {
    reviewCount,
    averageRating,
    lowRatingCount,
    recentLowCount,
    lastRatedAt,
  };
}

export function buildDriverRatingReport(drivers, reviewRows) {
  const normalizedDrivers = (drivers || []).map(normalizeDriverApplication);

  const reviewsByDriverKey = new Map();
  (reviewRows || []).forEach((review) => {
    const key = String(review.driverId || '');
    if (!key) return;
    if (!reviewsByDriverKey.has(key)) reviewsByDriverKey.set(key, []);
    reviewsByDriverKey.get(key).push(review);
  });

  const summaries = normalizedDrivers.map((driver) => {
    const keys = driverRatingIdentityKeys(driver);
    const merged = [];
    keys.forEach((key) => {
      if (reviewsByDriverKey.has(key)) {
        merged.push(...reviewsByDriverKey.get(key));
      }
    });
    const reviews = dedupeReviews(merged);
    const stats = summarizeReviews(reviews);
    const storedRatingRaw = Number(driver.rating);
    const storedRating = Number.isFinite(storedRatingRaw) ? storedRatingRaw : null;
    const storedTrips = Number(driver.trips);
    const profileTrips = Number.isFinite(storedTrips) ? storedTrips : null;

    const ratingMismatch =
      stats.averageRating != null &&
      storedRating != null &&
      Math.abs(stats.averageRating - storedRating) > 0.05;

    return {
      driver,
      reviews,
      ...stats,
      storedRating,
      profileTrips,
      ratingMismatch,
      identityKeys: keys,
    };
  });

  summaries.sort((a, b) => {
    const aAvg = a.averageRating ?? -1;
    const bAvg = b.averageRating ?? -1;
    if (a.reviewCount && b.reviewCount && aAvg !== bAvg) return aAvg - bAvg;
    if (a.reviewCount !== b.reviewCount) return b.reviewCount - a.reviewCount;
    return String(a.driver.full_name || '').localeCompare(String(b.driver.full_name || ''), undefined, {
      sensitivity: 'base',
    });
  });

  const recentReviews = summaries
    .flatMap((row) =>
      row.reviews.map((review) => ({
        ...review,
        driver: row.driver,
        summary: row,
      }))
    )
    .sort((a, b) => Date.parse(b.ratedAt || 0) - Date.parse(a.ratedAt || 0));

  const totals = {
    driversWithReviews: summaries.filter((s) => s.reviewCount > 0).length,
    totalReviews: summaries.reduce((n, s) => n + s.reviewCount, 0),
    driversBelowFour: summaries.filter(
      (s) => s.reviewCount > 0 && s.averageRating != null && s.averageRating < 4
    ).length,
    driversNeedingAttention: summaries.filter((s) => s.recentLowCount > 0).length,
  };

  return { summaries, recentReviews, totals };
}

export function summaryMatchesFilter(summary, filter) {
  if (filter === 'all') return true;
  if (filter === 'reviewed') return summary.reviewCount > 0;
  if (filter === 'below4') {
    return summary.reviewCount > 0 && summary.averageRating != null && summary.averageRating < 4;
  }
  if (filter === 'below3') {
    return summary.reviewCount > 0 && summary.averageRating != null && summary.averageRating < 3;
  }
  if (filter === 'attention') return summary.recentLowCount > 0;
  return true;
}

export function summaryMatchesSearch(summary, query) {
  return driverApplicationMatchesSearch(summary.driver, query);
}

async function loadRideRatingRows(supabaseClient) {
  const { data, error } = await supabaseClient
    .from('ride_ratings')
    .select('ride_id,rider_id,driver_id,stars,feedback,created_at')
    .order('created_at', { ascending: false })
    .limit(5000);

  if (!error) {
    return { rows: (data || []).map(normalizeReviewFromRatingRow), error: '' };
  }

  const message = error?.message || '';
  const missingTable =
    error?.code === '42P01' || /ride_ratings|schema cache|could not find/i.test(message);

  if (!missingTable) {
    return { rows: [], error: message };
  }

  const fallback = await supabaseClient
    .from('ride_requests')
    .select(
      'id,driver_id,rider_id,rider_rating,rider_feedback,rated_at,pickup,dropoff'
    )
    .not('rider_rating', 'is', null)
    .order('rated_at', { ascending: false })
    .limit(5000);

  if (fallback.error) {
    return { rows: [], error: fallback.error.message || 'Could not load ratings.' };
  }

  const rows = (fallback.data || [])
    .map(normalizeReviewFromRideRequest)
    .filter(Boolean);

  return { rows, error: '' };
}

export async function fetchDriverRatingReport(supabaseClient) {
  if (!supabaseClient) {
    return { report: null, error: 'Supabase is not configured.' };
  }

  const [driversResult, ratingLoad] = await Promise.all([
    supabaseClient
      .from('drivers')
      .select(
        'id,app_profile_id,full_name,name,phone,email,status,rating,trips,admin_notes,signup_source,city'
      )
      .in('status', ['approved', 'reviewing', 'deactivated'])
      .order('full_name', { ascending: true }),
    loadRideRatingRows(supabaseClient),
  ]);

  if (driversResult.error) {
    const msg = driversResult.error.message || 'Could not load drivers.';
    const hint =
      driversResult.error.code === '42501' || /permission|row-level/i.test(msg)
        ? ' Run supabase/admin-policies.sql in Supabase.'
        : '';
    return { report: null, error: msg + hint };
  }

  const ratingHint =
    ratingLoad.error && /permission|row-level|42501/i.test(ratingLoad.error)
      ? ' Run supabase/admin-driver-ratings.sql in Supabase.'
      : '';

  const report = buildDriverRatingReport(driversResult.data || [], ratingLoad.rows);

  return {
    report,
    error: (ratingLoad.error || '') + ratingHint,
  };
}

export async function updateDriverRatingAdminAction(supabaseClient, driverId, payload) {
  if (!supabaseClient || !driverId) {
    return { data: null, error: 'Missing driver.' };
  }
  const { data, error } = await supabaseClient
    .from('drivers')
    .update(payload)
    .eq('id', driverId)
    .select('*')
    .single();

  if (error) {
    return { data: null, error: error.message || 'Could not update driver.' };
  }
  return { data: normalizeDriverApplication(data), error: '' };
}
